import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Clock3,
  LoaderCircle,
  LockKeyhole,
  Mail,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

interface AdminUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  username: string | null;
  imageUrl: string;
  emailAddresses: string[];
  primaryEmailAddress: string | null;
  phoneNumbers: string[];
  createdAt: number;
  lastSignInAt: number | null;
  lastActiveAt: number | null;
  banned: boolean;
  locked: boolean;
  isAdmin: boolean;
}

interface AdminUsersResponse {
  users: AdminUser[];
  totalCount: number;
  limit: number;
  offset: number;
}

interface AdminRequestError extends Error {
  status?: number;
}

interface AccessChange {
  user: AdminUser;
  banned: boolean;
}

const PAGE_SIZE = 12;
const usersQueryKey = ["/api/admin/users"] as const;

async function readAdminResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = "The request could not be completed.";
    try {
      const body = await response.json();
      if (typeof body.error === "string") message = body.error;
      else if (typeof body.message === "string") message = body.message;
    } catch {
      // Keep a useful generic message for non-JSON responses.
    }
    const error = new Error(message) as AdminRequestError;
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

function accountName(user: AdminUser): string {
  const fullName = user.fullName?.trim();
  if (fullName) return fullName;
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (user.username) return `@${user.username.replace(/^@/, "")}`;
  return "Unnamed account";
}

function accountInitials(user: AdminUser): string {
  const name = accountName(user);
  if (name.startsWith("@")) return name.slice(1, 3).toUpperCase();
  return name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "U";
}

function formatDate(timestamp: number | null, includeTime = false): string {
  if (timestamp === null || !Number.isFinite(timestamp)) return "Never";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "Unavailable";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" as const } : {}),
  }).format(date);
}

function relativeTime(timestamp: number | null): string {
  if (timestamp === null || !Number.isFinite(timestamp)) return "No activity recorded";
  const minutes = Math.max(0, Math.floor((Date.now() - timestamp) / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(timestamp);
}

function StatusPill({ user }: { user: AdminUser }) {
  if (user.banned) {
    return <Badge variant="destructive" className="border-rose-400/15 bg-rose-400/10 text-rose-200 hover:bg-rose-400/10">Blocked</Badge>;
  }
  if (user.locked) {
    return <Badge variant="outline" className="border-amber-300/25 bg-amber-300/10 text-amber-200">Locked</Badge>;
  }
  return <Badge variant="outline" className="border-teal-300/20 bg-teal-300/[0.08] text-teal-200">Access active</Badge>;
}

export default function AdminDirectory() {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [accessChange, setAccessChange] = useState<AccessChange | null>(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchText.trim());
      setOffset(0);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  const usersQuery = useQuery<AdminUsersResponse, AdminRequestError>({
    queryKey: [...usersQueryKey, searchQuery, PAGE_SIZE, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (searchQuery) params.set("query", searchQuery);
      const response = await fetch(`/api/admin/users?${params.toString()}`, { credentials: "include" });
      return readAdminResponse<AdminUsersResponse>(response);
    },
  });

  const detailKey = [...usersQueryKey, "detail", selectedUserId] as const;
  const selectedUserQuery = useQuery<{ user: AdminUser }, AdminRequestError>({
    queryKey: detailKey,
    queryFn: async () => {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(selectedUserId!)}`, { credentials: "include" });
      return readAdminResponse<{ user: AdminUser }>(response);
    },
    enabled: Boolean(selectedUserId),
  });

  const updateAccess = useMutation({
    mutationFn: async ({ user, banned }: AccessChange) => {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(user.id)}/access`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banned }),
      });
      return readAdminResponse<{ user: AdminUser }>(response);
    },
    onSuccess: async ({ user }) => {
      queryClient.setQueryData([...usersQueryKey, "detail", user.id], { user });
      setSuccessMessage(`${accountName(user)} access ${user.banned ? "blocked" : "restored"}.`);
      setAccessChange(null);
      await queryClient.invalidateQueries({ queryKey: usersQueryKey });
    },
  });

  const closeConfirmation = (open: boolean) => {
    if (!open && !updateAccess.isPending) setAccessChange(null);
  };
  const listSummary = useMemo(() => {
    if (!usersQuery.data) return "Search Clerk accounts";
    return `${usersQuery.data.totalCount.toLocaleString()} accounts`;
  }, [usersQuery.data]);

  return (
    <section className="mt-7 grid gap-5 xl:grid-cols-[minmax(350px,0.82fr)_minmax(0,1.45fr)]">
      <div className="min-w-0 overflow-hidden rounded-2xl border border-[#2a393c] bg-[#162124]">
        <div className="border-b border-[#2a393c] p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Directory</p>
              <h2 className="mt-1 text-xl font-semibold text-[#edf2ee]">Accounts</h2>
            </div>
            <button
              type="button"
              aria-label="Refresh account list"
              onClick={() => void usersQuery.refetch()}
              disabled={usersQuery.isFetching}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#334448] text-slate-400 transition-colors hover:bg-[#202e31] hover:text-slate-100 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${usersQuery.isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
          <label htmlFor="account-search" className="sr-only">Search accounts</label>
          <div className="relative mt-5">
            <Search aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              id="account-search"
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                setSelectedUserId(null);
                setSuccessMessage("");
                updateAccess.reset();
              }}
              placeholder="Name, email, username, or ID"
              className="h-11 border-[#344649] bg-[#10191c] pl-10 text-sm placeholder:text-slate-600 focus-visible:ring-teal-400/60"
            />
            {searchText && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchText("")}
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 hover:bg-[#263639] hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <span>{listSummary}</span>
            <span>Clerk user records</span>
          </div>
        </div>

        <div className="min-h-[340px]">
          {usersQuery.isPending ? (
            <div className="space-y-1 p-3" aria-label="Loading accounts" aria-live="polite">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="flex animate-pulse items-center gap-3 rounded-xl p-3">
                  <div className="h-10 w-10 rounded-full bg-[#29383b]" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-2/5 rounded bg-[#29383b]" />
                    <div className="h-2.5 w-3/5 rounded bg-[#223033]" />
                  </div>
                </div>
              ))}
            </div>
          ) : usersQuery.isError ? (
            <div className="px-6 py-12 text-center" role="alert">
              <AlertTriangle className="mx-auto h-6 w-6 text-amber-300" />
              <p className="mt-3 text-sm font-medium text-slate-200">Accounts could not be loaded</p>
              <p className="mx-auto mt-1 max-w-xs text-xs leading-5 text-slate-500">{usersQuery.error.message}</p>
              <button type="button" onClick={() => void usersQuery.refetch()} className="mt-4 rounded-md border border-[#3b4c4f] px-3 py-2 text-xs font-medium text-slate-200 hover:bg-[#223033]">
                Try again
              </button>
            </div>
          ) : usersQuery.data?.users.length ? (
            <div className="p-2">
              {usersQuery.data.users.map((user) => (
                <button
                  type="button"
                  key={user.id}
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setSuccessMessage("");
                    updateAccess.reset();
                  }}
                  aria-current={selectedUserId === user.id ? "true" : undefined}
                  className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                    selectedUserId === user.id
                      ? "bg-[#26373a] ring-1 ring-inset ring-[#40575a]"
                      : "hover:bg-[#1e2c2f]"
                  }`}
                >
                  <AccountAvatar key={user.id} user={user} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-slate-100">{accountName(user)}</span>
                      {user.isAdmin && <span className="shrink-0 rounded border border-amber-300/20 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-200">Admin</span>}
                    </span>
                    <span className="mt-1 block truncate text-xs text-slate-500">{user.primaryEmailAddress || (user.username ? `@${user.username.replace(/^@/, "")}` : user.id)}</span>
                  </span>
                  <span className="hidden shrink-0 sm:block"><StatusPill user={user} /></span>
                  <ChevronRight className={`h-4 w-4 shrink-0 transition-colors ${selectedUserId === user.id ? "text-teal-300" : "text-slate-600 group-hover:text-slate-400"}`} />
                </button>
              ))}
            </div>
          ) : (
            <div className="px-6 py-14 text-center">
              <Search className="mx-auto h-6 w-6 text-slate-600" />
              <p className="mt-3 text-sm font-medium text-slate-300">{searchQuery ? "No matching accounts" : "No accounts found"}</p>
              <p className="mt-1 text-xs text-slate-500">{searchQuery ? "Try a different name, email, username, or ID." : "There are no Clerk accounts to show."}</p>
            </div>
          )}
        </div>

        {usersQuery.data && usersQuery.data.totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-[#2a393c] px-4 py-3">
            <p className="text-xs tabular-nums text-slate-500">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, usersQuery.data.totalCount)} of {usersQuery.data.totalCount}
            </p>
            <div className="flex gap-1">
              <button type="button" aria-label="Previous page" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="flex h-8 w-8 items-center justify-center rounded-md border border-[#344649] text-slate-300 hover:bg-[#223033] disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" aria-label="Next page" disabled={offset + PAGE_SIZE >= usersQuery.data.totalCount} onClick={() => setOffset(offset + PAGE_SIZE)} className="flex h-8 w-8 items-center justify-center rounded-md border border-[#344649] text-slate-300 hover:bg-[#223033] disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <section className="min-h-[500px] min-w-0 overflow-hidden rounded-2xl border border-[#2a393c] bg-[#162124]" aria-label="Selected account details">
        {!selectedUserId ? (
          <div className="flex min-h-[500px] flex-col items-center justify-center px-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#334448] bg-[#1c2a2d] text-slate-500">
              <CircleUserRound className="h-6 w-6" />
            </div>
            <h2 className="mt-5 text-lg font-semibold text-slate-200">Select an account</h2>
            <p className="mt-2 max-w-xs text-sm leading-6 text-slate-500">Choose a person from the directory to review account details and access status.</p>
          </div>
        ) : selectedUserQuery.isPending ? (
          <div className="animate-pulse p-6" aria-label="Loading account details" aria-live="polite">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-2xl bg-[#29383b]" />
              <div className="space-y-3"><div className="h-4 w-48 rounded bg-[#29383b]" /><div className="h-3 w-32 rounded bg-[#223033]" /></div>
            </div>
            <div className="mt-8 h-32 rounded-xl bg-[#1e2c2f]" />
            <div className="mt-4 h-40 rounded-xl bg-[#1e2c2f]" />
          </div>
        ) : selectedUserQuery.isError ? (
          <div className="flex min-h-[500px] flex-col items-center justify-center px-6 text-center" role="alert">
            <AlertTriangle className="h-6 w-6 text-amber-300" />
            <h2 className="mt-4 text-base font-semibold text-slate-200">Account details unavailable</h2>
            <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">{selectedUserQuery.error.message}</p>
            <button type="button" onClick={() => void selectedUserQuery.refetch()} className="mt-5 rounded-md border border-[#3b4c4f] px-3 py-2 text-xs font-medium text-slate-200 hover:bg-[#223033]">Retry details</button>
          </div>
        ) : selectedUserQuery.data ? (
          <AccountDetails
            user={selectedUserQuery.data.user}
            successMessage={successMessage}
            mutationError={updateAccess.isError ? updateAccess.error.message : ""}
            isSaving={updateAccess.isPending}
            onAccessChange={() => {
              const user = selectedUserQuery.data!.user;
              setSuccessMessage("");
              updateAccess.reset();
              setAccessChange({ user, banned: !user.banned });
            }}
          />
        ) : null}
      </section>

      <AlertDialog open={Boolean(accessChange)} onOpenChange={closeConfirmation}>
        <AlertDialogContent className="border-[#344649] bg-[#182427] text-slate-100 sm:rounded-2xl">
          <AlertDialogHeader>
            <p className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${accessChange?.banned ? "text-rose-300" : "text-teal-300"}`}>
              Confirm access change
            </p>
            <AlertDialogTitle className="text-xl text-slate-100">
              {accessChange?.banned ? "Block account access?" : "Restore account access?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-1 text-left leading-6 text-slate-400">
              <span className="block">
                {accessChange?.banned
                  ? "This will block sign-in and revoke active sessions for "
                  : "This will remove the ban and allow sign-in for "}
                <strong className="font-semibold text-slate-100">{accessChange ? accountName(accessChange.user) : ""}</strong>.
              </span>
              {accessChange && <span className="block break-all rounded-lg border border-[#334448] bg-[#111a1d] px-3 py-2 font-mono text-xs text-slate-400">{accessChange.user.primaryEmailAddress || accessChange.user.id}</span>}
              <span className="block text-xs text-slate-500">
                This changes ban status only. It does not delete the account or change its profile. A separate security lock may still prevent sign-in until it expires or is removed.
              </span>
            </AlertDialogDescription>
            {updateAccess.isError && (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-300/20 bg-rose-300/[0.06] px-3 py-2.5 text-xs leading-5 text-rose-100">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Access was not changed. {updateAccess.error.message}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 gap-2 sm:gap-2">
            <AlertDialogCancel disabled={updateAccess.isPending} className="border-[#405256] bg-transparent text-slate-200 hover:bg-[#243336] sm:mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!accessChange || updateAccess.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (accessChange && !accessChange.user.isAdmin) updateAccess.mutate(accessChange);
              }}
              className={accessChange?.banned
                ? "border border-rose-400/30 bg-rose-700 text-white hover:bg-rose-600"
                : "border border-teal-300/25 bg-teal-700 text-white hover:bg-teal-600"}
            >
              {updateAccess.isPending ? <><LoaderCircle className="h-4 w-4 animate-spin" /> Saving…</> : accessChange?.banned ? "Block access" : "Restore access"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function AccountAvatar({ user, size = "lg" }: { user: AdminUser; size?: "sm" | "lg" }) {
  const [imageFailed, setImageFailed] = useState(false);
  const sizeClass = size === "lg" ? "h-16 w-16 rounded-2xl text-lg" : "h-10 w-10 rounded-xl text-xs";
  if (user.imageUrl && !imageFailed) {
    return <img src={user.imageUrl} alt="" onError={() => setImageFailed(true)} className={`${sizeClass} shrink-0 border border-white/10 object-cover`} />;
  }
  return <span aria-hidden="true" className={`${sizeClass} flex shrink-0 items-center justify-center border border-[#46595c] bg-[#26373a] font-semibold text-teal-100`}>{accountInitials(user)}</span>;
}

function AccountDetails({
  user,
  successMessage,
  mutationError,
  isSaving,
  onAccessChange,
}: {
  user: AdminUser;
  successMessage: string;
  mutationError: string;
  isSaving: boolean;
  onAccessChange: () => void;
}) {
  const emails = useMemo(() => {
    const addresses = user.emailAddresses ?? [];
    if (user.primaryEmailAddress && !addresses.includes(user.primaryEmailAddress)) {
      return [user.primaryEmailAddress, ...addresses];
    }
    return addresses;
  }, [user.emailAddresses, user.primaryEmailAddress]);
  const blocked = user.banned;

  return (
    <div>
      <div className="border-b border-[#2a393c] p-5 sm:p-7">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <AccountAvatar key={user.id} user={user} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-xl font-semibold tracking-tight text-[#eff3ef] sm:text-2xl">{accountName(user)}</h2>
                {user.isAdmin && <Badge variant="outline" className="border-amber-300/25 bg-amber-300/[0.08] text-amber-200">Administrator</Badge>}
              </div>
              {user.username && <p className="mt-1 text-sm text-slate-400">@{user.username.replace(/^@/, "")}</p>}
              <div className="mt-2"><StatusPill user={user} /></div>
            </div>
          </div>
          <button
            type="button"
            disabled={user.isAdmin || isSaving}
            onClick={onAccessChange}
            title={user.isAdmin ? "Administrator accounts cannot have access changed here." : undefined}
            className={`inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto ${
              blocked
                ? "border-teal-300/25 bg-teal-400/10 text-teal-100 hover:bg-teal-400/15"
                : "border-rose-300/25 bg-rose-400/[0.08] text-rose-100 hover:bg-rose-400/[0.14]"
            }`}
          >
            {blocked ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
            {blocked ? "Restore access" : "Block access"}
          </button>
        </div>

        {user.isAdmin && (
          <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-amber-300/15 bg-amber-300/[0.06] px-3.5 py-3 text-xs leading-5 text-amber-100/80">
            <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" />
            <p><span className="font-semibold text-amber-100">Protected administrator account.</span> Access changes are disabled for admin accounts.</p>
          </div>
        )}
        {successMessage && (
          <div role="status" aria-live="polite" className="mt-5 flex items-center gap-2 rounded-lg border border-teal-300/20 bg-teal-300/[0.07] px-3.5 py-3 text-sm text-teal-100">
            <Check className="h-4 w-4 shrink-0" /> {successMessage}
          </div>
        )}
        {mutationError && (
          <div role="alert" className="mt-5 flex items-start gap-2 rounded-lg border border-rose-300/20 bg-rose-300/[0.06] px-3.5 py-3 text-sm text-rose-100">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Access was not changed. {mutationError}</span>
          </div>
        )}
      </div>

      <div className="grid gap-5 p-5 sm:p-7 lg:grid-cols-[1fr_0.9fr]">
        <section>
          <SectionLabel icon={<UserRound className="h-4 w-4" />} title="Account identity" />
          <div className="mt-3 overflow-hidden rounded-xl border border-[#2c3c3f] bg-[#121c1f]">
            <InfoRow label="Full name" value={accountName(user)} />
            {user.username && <InfoRow label="Username" value={`@${user.username.replace(/^@/, "")}`} />}
            <InfoRow label="Account ID" value={user.id} mono />
            <InfoRow label="Account created" value={formatDate(user.createdAt, true)} icon={<CalendarDays className="h-3.5 w-3.5" />} />
          </div>

          <SectionLabel className="mt-6" icon={<Mail className="h-4 w-4" />} title="Email addresses" count={emails.length} />
          <div className="mt-3 overflow-hidden rounded-xl border border-[#2c3c3f] bg-[#121c1f]">
            {emails.length ? emails.map((email) => (
              <div key={email} className="flex min-w-0 items-center justify-between gap-3 border-b border-[#263538] px-4 py-3 last:border-0">
                <span className="min-w-0 break-all text-sm text-slate-200">{email}</span>
                {email === user.primaryEmailAddress && <span className="shrink-0 rounded border border-teal-300/20 bg-teal-300/[0.07] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-teal-200">Primary</span>}
              </div>
            )) : <EmptyValue>No email address on file</EmptyValue>}
          </div>

          <SectionLabel className="mt-6" icon={<Phone className="h-4 w-4" />} title="Phone numbers" count={user.phoneNumbers.length} />
          <div className="mt-3 overflow-hidden rounded-xl border border-[#2c3c3f] bg-[#121c1f]">
            {user.phoneNumbers.length ? user.phoneNumbers.map((phone) => <InfoRow key={phone} label="Phone" value={phone} />) : <EmptyValue>No phone number on file</EmptyValue>}
          </div>
        </section>

        <section>
          <SectionLabel icon={<ShieldCheck className="h-4 w-4" />} title="Access and activity" />
          <div className="mt-3 overflow-hidden rounded-xl border border-[#2c3c3f] bg-[#121c1f]">
            <div className="flex items-center justify-between gap-4 border-b border-[#263538] px-4 py-3.5">
              <span className="text-xs text-slate-500">Sign-in access</span>
              <StatusPill user={user} />
            </div>
            <InfoRow label="Account lock" value={user.locked ? "Locked" : "Not locked"} />
            <InfoRow label="Administrator" value={user.isAdmin ? "Yes · protected" : "No"} />
          </div>
          <div className="mt-4 grid gap-3">
            <ActivityCard icon={<Clock3 className="h-4 w-4" />} title="Last sign-in" value={formatDate(user.lastSignInAt, true)} detail={relativeTime(user.lastSignInAt)} />
            <ActivityCard icon={<Activity className="h-4 w-4" />} title="Last active" value={formatDate(user.lastActiveAt, true)} detail={relativeTime(user.lastActiveAt)} />
          </div>
          <div className="mt-4 rounded-xl border border-[#2c3c3f] bg-[#121c1f] px-4 py-3">
            <p className="text-xs font-medium text-slate-300">Access changes are explicit</p>
            <p className="mt-1.5 text-xs leading-5 text-slate-500">Blocking prevents sign-in. Restoring returns this account to active access. Both actions require confirmation.</p>
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionLabel({ icon, title, count, className = "" }: { icon: ReactNode; title: string; count?: number; className?: string }) {
  return (
    <div className={`flex items-center gap-2 text-slate-400 ${className}`}>
      <span className="text-teal-300/80">{icon}</span>
      <h3 className="text-sm font-semibold text-slate-200">{title}</h3>
      {count !== undefined && <span className="rounded border border-[#354649] px-1.5 py-0.5 text-[10px] tabular-nums text-slate-500">{count}</span>}
    </div>
  );
}

function InfoRow({ label, value, mono = false, icon }: { label: string; value: string; mono?: boolean; icon?: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center justify-between gap-4 border-b border-[#263538] px-4 py-3 last:border-0">
      <span className="shrink-0 text-xs text-slate-500">{label}</span>
      <span className={`flex min-w-0 items-center gap-1.5 text-right text-sm text-slate-200 ${mono ? "break-all font-mono text-[11px]" : ""}`}>
        {icon && <span className="shrink-0 text-slate-500">{icon}</span>}
        {value}
      </span>
    </div>
  );
}

function EmptyValue({ children }: { children: ReactNode }) {
  return <p className="px-4 py-3 text-xs text-slate-600">{children}</p>;
}

function ActivityCard({ icon, title, value, detail }: { icon: ReactNode; title: string; value: string; detail: string }) {
  return (
    <div className="rounded-xl border border-[#2c3c3f] bg-[#121c1f] px-4 py-3.5">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-400"><span className="text-teal-300/80">{icon}</span>{title}</div>
      <p className="mt-3 text-sm font-medium text-slate-100">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{detail}</p>
    </div>
  );
}
